import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOrdenesPermisos } from '../../hooks/useOrdenesPermisos';
import { ordenesImportacionAPI } from '../../lib/ordenesApi';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Progress } from '../../components/ui/progress';
import {
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
} from '../../components/ui/tabs';
import {
    Alert,
    AlertDescription,
    AlertTitle,
} from '../../components/ui/alert';
import { toast } from 'sonner';
import {
    Upload,
    Download,
    ArrowLeft,
    FileSpreadsheet,
    Users,
    Car,
    Package,
    Loader2,
    CheckCircle,
    XCircle,
    AlertTriangle,
    Info,
} from 'lucide-react';

const TIPOS_IMPORTACION = [
    {
        id: 'clientes',
        nombre: 'Clientes',
        descripcion: 'Importar empresas/clientes con sus datos de contacto',
        icon: Users,
        color: 'bg-blue-100 text-blue-700',
        campos: ['rut*', 'razon_social*', 'nombre_fantasia', 'direccion', 'comuna', 'ciudad', 'telefono', 'email', 'contacto_principal', 'giro'],
    },
    {
        id: 'vehiculos',
        nombre: 'Vehículos',
        descripcion: 'Importar vehículos asociados a clientes existentes',
        icon: Car,
        color: 'bg-green-100 text-green-700',
        campos: ['rut_cliente*', 'patente*', 'tipo_vehiculo', 'marca', 'modelo', 'anio', 'color', 'vin'],
    },
    {
        id: 'inventario',
        nombre: 'Inventario',
        descripcion: 'Importar dispositivos al stock',
        icon: Package,
        color: 'bg-purple-100 text-purple-700',
        campos: ['tipo_dispositivo*', 'serial*', 'marca', 'modelo', 'estado', 'observaciones'],
    },
];

export default function OrdenesImportacionPage() {
    const navigate = useNavigate();
    const { tienePermiso, loading: permisosLoading } = useOrdenesPermisos();
    const [activeTab, setActiveTab] = useState('clientes');
    const [uploading, setUploading] = useState(false);
    const [resultado, setResultado] = useState(null);
    const fileInputRef = useRef(null);

    const handleDescargarPlantilla = async (tipo) => {
        try {
            const response = await ordenesImportacionAPI.descargarPlantilla(tipo);
            const blob = new Blob([response.data], { 
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
            });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `plantilla_${tipo}.xlsx`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            toast.success('Plantilla descargada');
        } catch (err) {
            toast.error('Error al descargar plantilla');
        }
    };

    const handleFileSelect = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        
        if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
            toast.error('Solo se permiten archivos Excel (.xlsx, .xls)');
            return;
        }
        
        setUploading(true);
        setResultado(null);
        
        try {
            let response;
            switch (activeTab) {
                case 'clientes':
                    response = await ordenesImportacionAPI.importarClientes(file);
                    break;
                case 'vehiculos':
                    response = await ordenesImportacionAPI.importarVehiculos(file);
                    break;
                case 'inventario':
                    response = await ordenesImportacionAPI.importarInventario(file);
                    break;
                default:
                    throw new Error('Tipo no válido');
            }
            
            setResultado(response.data);
            
            if (response.data.errores?.length > 0) {
                toast.warning('Importación completada con algunos errores');
            } else {
                toast.success('Importación completada exitosamente');
            }
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Error al importar archivo');
            setResultado({ errores: [err.response?.data?.detail || 'Error desconocido'] });
        } finally {
            setUploading(false);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    const triggerFileInput = () => {
        fileInputRef.current?.click();
    };

    if (permisosLoading) {
        return (
            <div className="space-y-6 p-6">
                <Skeleton className="h-10 w-64" />
                <Skeleton className="h-96 w-full" />
            </div>
        );
    }

    const tipoActual = TIPOS_IMPORTACION.find(t => t.id === activeTab);
    const Icon = tipoActual?.icon || FileSpreadsheet;

    return (
        <div className="space-y-6 p-6">
            {/* Header */}
            <div className="flex items-center gap-4">
                <Button variant="ghost" size="icon" onClick={() => navigate('/ordenes/dashboard')} data-testid="back-btn">
                    <ArrowLeft className="h-5 w-5" />
                </Button>
                <div>
                    <h1 className="text-2xl font-bold flex items-center gap-2">
                        <Upload className="h-6 w-6" />
                        Importación Masiva
                    </h1>
                    <p className="text-muted-foreground">Carga datos desde archivos Excel</p>
                </div>
            </div>

            {/* Info Alert */}
            <Alert>
                <Info className="h-4 w-4" />
                <AlertTitle>Cómo importar datos</AlertTitle>
                <AlertDescription>
                    1. Descarga la plantilla Excel del tipo de dato que deseas importar.<br />
                    2. Completa los datos siguiendo el formato de ejemplo.<br />
                    3. Sube el archivo completado. Los registros existentes se actualizarán automáticamente.
                </AlertDescription>
            </Alert>

            <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); setResultado(null); }}>
                <TabsList className="grid grid-cols-3 w-full max-w-lg">
                    {TIPOS_IMPORTACION.map(tipo => (
                        <TabsTrigger key={tipo.id} value={tipo.id} className="gap-2">
                            <tipo.icon className="h-4 w-4" />
                            {tipo.nombre}
                        </TabsTrigger>
                    ))}
                </TabsList>

                {TIPOS_IMPORTACION.map(tipo => (
                    <TabsContent key={tipo.id} value={tipo.id} className="space-y-4">
                        <Card className="border-2">
                            <CardHeader>
                                <div className="flex items-start gap-4">
                                    <div className={`p-3 rounded-lg ${tipo.color}`}>
                                        <tipo.icon className="h-6 w-6" />
                                    </div>
                                    <div>
                                        <CardTitle>{tipo.nombre}</CardTitle>
                                        <CardDescription>{tipo.descripcion}</CardDescription>
                                    </div>
                                </div>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                {/* Campos */}
                                <div>
                                    <p className="text-sm font-medium mb-2">Campos disponibles:</p>
                                    <div className="flex flex-wrap gap-2">
                                        {tipo.campos.map(campo => (
                                            <Badge 
                                                key={campo} 
                                                variant={campo.endsWith('*') ? 'default' : 'outline'}
                                            >
                                                {campo.replace('*', '')}
                                                {campo.endsWith('*') && <span className="text-red-300 ml-1">*</span>}
                                            </Badge>
                                        ))}
                                    </div>
                                    <p className="text-xs text-muted-foreground mt-2">* Campos obligatorios</p>
                                </div>

                                {/* Acciones */}
                                <div className="flex flex-col sm:flex-row gap-4 pt-4 border-t">
                                    <Button
                                        variant="outline"
                                        onClick={() => handleDescargarPlantilla(tipo.id)}
                                        className="flex-1"
                                    >
                                        <Download className="h-4 w-4 mr-2" />
                                        Descargar Plantilla
                                    </Button>
                                    
                                    <input
                                        type="file"
                                        ref={fileInputRef}
                                        onChange={handleFileSelect}
                                        accept=".xlsx,.xls"
                                        className="hidden"
                                    />
                                    
                                    <Button
                                        onClick={triggerFileInput}
                                        disabled={uploading}
                                        className="flex-1"
                                    >
                                        {uploading ? (
                                            <>
                                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                                Importando...
                                            </>
                                        ) : (
                                            <>
                                                <Upload className="h-4 w-4 mr-2" />
                                                Subir Archivo
                                            </>
                                        )}
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>

                        {/* Resultado */}
                        {resultado && (
                            <Card className="border-2">
                                <CardHeader>
                                    <CardTitle className="text-lg flex items-center gap-2">
                                        {resultado.errores?.length > 0 ? (
                                            <AlertTriangle className="h-5 w-5 text-yellow-500" />
                                        ) : (
                                            <CheckCircle className="h-5 w-5 text-green-500" />
                                        )}
                                        Resultado de la Importación
                                    </CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    {/* Estadísticas */}
                                    <div className="grid grid-cols-3 gap-4">
                                        <div className="text-center p-4 bg-green-50 rounded-lg">
                                            <p className="text-2xl font-bold text-green-700">{resultado.creados || 0}</p>
                                            <p className="text-sm text-green-600">Creados</p>
                                        </div>
                                        <div className="text-center p-4 bg-blue-50 rounded-lg">
                                            <p className="text-2xl font-bold text-blue-700">{resultado.actualizados || 0}</p>
                                            <p className="text-sm text-blue-600">Actualizados</p>
                                        </div>
                                        <div className="text-center p-4 bg-red-50 rounded-lg">
                                            <p className="text-2xl font-bold text-red-700">{resultado.errores?.length || 0}</p>
                                            <p className="text-sm text-red-600">Errores</p>
                                        </div>
                                    </div>

                                    {/* Errores */}
                                    {resultado.errores?.length > 0 && (
                                        <div className="space-y-2">
                                            <p className="font-medium text-sm">Detalle de errores:</p>
                                            <div className="max-h-48 overflow-y-auto space-y-1 bg-red-50 p-3 rounded-lg">
                                                {resultado.errores.map((error, idx) => (
                                                    <div key={idx} className="flex items-start gap-2 text-sm">
                                                        <XCircle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
                                                        <span className="text-red-700">{error}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </CardContent>
                            </Card>
                        )}
                    </TabsContent>
                ))}
            </Tabs>
        </div>
    );
}
